/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { CheckIn, CheckinStatus, DockingStatus, UpdateCheckinData } from '../models/client/checkins'
import {
  ApiCheckin,
  ApiCheckinHistory,
  ApiCheckinStatus,
  ApiCheckinUpdate,
  ApiDockOptions,
  ApiFuelTypes,
} from '../models/api'
import { apiRequest } from './apiRequest'
import { ValidationError } from '../util/errors/ValidationError'

const getCheckinLists = async ({
  token,
  companyId,
  siteId,
  locationId,
}: {
  token: string
  companyId: string
  siteId: string
  locationId: string
}): Promise<CheckIn[]> => {
  const currentDate = new Date()
  currentDate.setUTCDate(currentDate.getUTCDate() - 1)
  let filter = {
    companyId,
    siteId,
    $and: {
      'status.key': { $in: ['ingated', 'docking'] },
      $or: {
        'status.key': 'outgated',
        created_at: ['>', currentDate.toISOString()],
      },
    },
    archivedAt: {
      $isnull: true,
    },
  }
  if (locationId) {
    // @ts-ignore
    filter = { ...filter, locationId }
  }
  const { data } = await apiRequest<ApiCheckin>({
    path: '/driver_checkins/search',
    token,
    method: 'POST',
    include: ['dock', 'conversation.sms'],
    parameters: {
      filter,
      sort: 'createdAt desc',
    },
  })

  const checkins: CheckIn[] = []
  data.forEach((item: CheckIn) => {
    try {
      checkins.push(item)
    } catch (e) {
      if (e instanceof ValidationError) {
        return
      }
      throw e
    }
  })
  return checkins
}

const searchCheckins = async ({
  token,
  companyId,
  siteId,
  searchTerm,
  page,
  size,
}: {
  token: string
  companyId: string
  siteId: string
  searchTerm: string
  page: number
  size: number
}): Promise<CheckIn[]> => {
  let filter
  filter = {
    companyId,
    archivedAt: {
      $isnull: true,
    },
  }
  if (siteId) {
    filter = { ...filter, siteId }
  }
  if (searchTerm) {
    filter = {
      ...filter,
      $and: {
        asset_number: { $ilike: `%${searchTerm}%` },
        $or: {
          load_number: { $ilike: `%${searchTerm}%` },
          $or: {
            phone_number: { $ilike: `%${searchTerm}%` },
          },
        },
      },
    }
  }

  const response = await apiRequest<ApiCheckinHistory>({
    path: '/driver_checkins/search',
    token,
    method: 'POST',
    include: ['dock', 'status'],
    data: {
      filter,
      page: {
        number: page,
        size,
      },
      sort: 'createdAt desc',
    },
  })
  return response
}

const getCheckinStatusList = async ({ token }: { token: string }): Promise<CheckinStatus[]> => {
  const { data } = await apiRequest<ApiCheckinStatus>({
    path: '/checkin_statuses',
    token,
    method: 'GET',
  })

  const checkinStatus: CheckinStatus[] = []

  data.forEach((item: CheckinStatus) => {
    try {
      checkinStatus.push(item)
    } catch (e) {
      if (e instanceof ValidationError) {
        return
      }
      throw e
    }
  })

  return checkinStatus
}

const updateCheckin = async ({
  token,
  checkinId,
  params,
}: {
  token: string
  checkinId: string
  params: UpdateCheckinData
}): Promise<any> => {
  const { data } = await apiRequest<ApiCheckinUpdate>({
    method: 'PATCH',
    path: `/driver_checkins/${checkinId}`,
    token,
    include: ['site', 'status', 'company', 'dock'],
    data: { sendSMS: !!params.dockId, ...params },
  })

  return data
}

const getFuelTypes = async (): Promise<any> => {
  const { data: fuelTypes } = await apiRequest<ApiFuelTypes>({
    path: '/fuel_types',
    method: 'GET',
  })

  const res = {}
  for (const fuelType of fuelTypes || []) {
    res[fuelType.id] = `${fuelType.name}`
  }

  return res
}

const getFuelTypesWithAttributes = async (): Promise<any> => {
  const { data: fuelTypes } = await apiRequest<ApiFuelTypes>({
    path: '/fuel_types',
    method: 'GET',
  })

  const res: Array<{ name: string; id: string }> = []
  for (const fuelType of fuelTypes || []) {
    res.push({ name: fuelType.name, id: fuelType.key })
  }

  return res
}

const getDockingOptions = async ({
  token,
  companyId,
  siteId,
}: {
  companyId: string
  token: string
  siteId: string
}): Promise<any> => {
  const { data } = await apiRequest<ApiDockOptions>({
    path: '/docks',
    token,
    method: 'GET',
    include: ['site', 'company'],
    parameters: {
      filter: {
        companyId,
        siteId,
      },
    },
  })

  const dockingStatus = {}

  data.forEach((item: DockingStatus) => {
    try {
      dockingStatus[item.id] = item.meta?.name
    } catch (e) {
      if (e instanceof ValidationError) {
        return
      }
      throw e
    }
  })

  return dockingStatus
}

export const driverClient = {
  getCheckinStatusList,
  searchCheckins,
  getCheckinLists,
  updateCheckin,
  getDockingOptions,
  getFuelTypes,
  getFuelTypesWithAttributes,
}
